import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const LoginHandler: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const hasAttempted = React.useRef(false);
  const verifyLock = React.useRef(false);

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setError('邀请链接无效或缺失 Token。');
      setLoading(false);
      return;
    }

    if (hasAttempted.current || verifyLock.current) return;
    verifyLock.current = true;

    const verifyToken = async () => {
      try {
        // 从 localStorage 获取设备指纹（Device ID）
        const deviceId = localStorage.getItem('terminal_device_id');
        
        const url = deviceId 
            ? `/invitation/verify?token=${token}&device_id=${deviceId}` 
            : `/invitation/verify?token=${token}`;
          
        const response = await api.post(url);
        const { access_token, role, device_id: returnedDeviceId } = response.data;

        // 如果后端返回了新的（或确认了旧的）device_id，存入持久存储
        if (returnedDeviceId) {
          localStorage.setItem('terminal_device_id', returnedDeviceId);
        }

        // 使用 AuthContext 的 login 方法保存状态
        login(access_token, role, 'invitation');
        hasAttempted.current = true;

        toast.success(t('auth.toast_login_success') || '登录成功！正在跳转...');
        
        // 丝滑跳转到首页面（Dashboard）
        setTimeout(() => {
          if (role === 'admin') {
            navigate('/admin/dashboard', { replace: true });
          } else if (role === 'doctor') {
            navigate('/doctor/dashboard', { replace: true });
          } else if (response.data.is_new_user) {
            navigate('/welcome', { replace: true });
          } else {
            navigate('/mypage', { replace: true });
          }
        }, 1500);
      } catch (err: any) {
        console.error('Token verification failed:', err);
        hasAttempted.current = true;
        const detail = err.response?.data?.detail;
        if (detail === 'TOKEN_ALREADY_USED') {
          setError('该邀请码已失效，请联系客服。');
        } else if (detail === 'TOKEN_EXPIRED') {
          setError('该邀请码已过期。');
        } else if (detail === 'UNAUTHORIZED_USER') {
          setError('现在只能绑定一台设备，如果有问题请联系管理员微信');
        } else {
          setError('认证失败，请检查链接或联系管理员。');
        }
        setLoading(false);
      }
    };

    // 直接发起验证（不再强制走微信 OAuth）
    verifyToken();
  }, [searchParams, login, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral mb-4"></div>
          <p className="text-gray-600 text-lg">正在验证令牌，请稍候...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8 text-center border-t-4 border-red-500">
          <div className="flex justify-center mb-4">
            <svg className="w-16 h-16 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">登录失败</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => {
              // @ts-ignore
              if (typeof WeixinJSBridge !== 'undefined') {
                // @ts-ignore
                WeixinJSBridge.call('closeWindow');
              } else {
                window.location.href = '/';
              }
            }}
            className="w-full bg-coral hover:opacity-90 text-white font-bold py-3 px-4 rounded-lg transition duration-200"
          >
            关闭页面 / 返回首页
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default LoginHandler;
